import { EnvironmentProviders, Provider } from '@angular/core';
import { provideCurrencyDomain } from './currency.providers';
import { provideCurrencyRateDomain } from './currency-rate.providers';
import { provideCurrencyExchangeDomain } from './currency-exchange.providers';
import { provideSettingsDomain } from './settings.providers';
import { provideAccountDomain } from './account.providers';
import { provideAccountTransferDomain } from './account-transfer.providers';
import { provideCategoryDomain } from './category.providers';
import { provideIncomeDomain } from './income.providers';
import { provideExpenseDomain } from './expense.providers';
import { provideCreditorDomain } from './creditor.providers';
import { provideDebtorDomain } from './debtor.providers';
import { provideReportDomain } from './report.providers';
import { provideBackupDomain } from './backup.providers';
import { providePlannedExpenseDomain } from './planned-expense.providers';
import { providePlannedIncomeDomain } from './planned-income.providers';
import { provideBankImportDomain } from './bank-import.providers';
import { provideTransferMatchingDomain } from './transfer-matching.providers';
import { provideAuthDomain } from './auth.providers';
import { provideDekDomain } from './dek.providers';
import { provideTagDomain } from './tag.providers';

/**
 * Provides all domain layer services and use cases.
 *
 * This aggregates feature-specific domain providers into a single
 * function for convenient app-wide setup.
 *
 * Organization:
 * - Each feature (Currency, Currency Rate, Currency Exchange, Settings, Account, Category, etc.) has its own provider file
 * - This index aggregates them for easy import in app.config.ts
 *
 * Usage:
 * ```typescript
 * // Option 1: Import all domain providers
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     ...provideDomain(),
 *   ]
 * };
 *
 * // Option 2: Import specific features only
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     ...provideCurrencyDomain(),
 *     ...provideCurrencyRateDomain(),
 *     ...provideCurrencyExchangeDomain(),
 *     ...provideSettingsDomain(),
 *     ...provideAccountDomain(),
 *     ...provideCategoryDomain(),
 *   ]
 * };
 * ```
 */
export function provideDomain(): (EnvironmentProviders | Provider)[] {
  return [
    ...provideCurrencyDomain(),
    ...provideCurrencyRateDomain(),
    ...provideCurrencyExchangeDomain(),
    ...provideSettingsDomain(),
    ...provideAccountDomain(),
    ...provideAccountTransferDomain(),
    ...provideCategoryDomain(),
    ...provideIncomeDomain(),
    ...provideExpenseDomain(),
    ...provideCreditorDomain(),
    ...provideDebtorDomain(),
    ...provideReportDomain(),
    ...provideBackupDomain(),
    ...providePlannedExpenseDomain(),
    ...providePlannedIncomeDomain(),
    ...provideTransferMatchingDomain(),
    ...provideBankImportDomain(),
    ...provideAuthDomain(),
    ...provideDekDomain(),
    ...provideTagDomain(),
  ];
}

// Re-export feature-specific providers for granular imports
export { provideCurrencyDomain } from './currency.providers';
export { provideCurrencyRateDomain } from './currency-rate.providers';
export { provideCurrencyExchangeDomain } from './currency-exchange.providers';
export { provideSettingsDomain } from './settings.providers';
export { provideAccountDomain } from './account.providers';
export { provideAccountTransferDomain } from './account-transfer.providers';
export { provideCategoryDomain } from './category.providers';
export { provideIncomeDomain } from './income.providers';
export { provideExpenseDomain } from './expense.providers';
export { provideCreditorDomain } from './creditor.providers';
export { provideDebtorDomain } from './debtor.providers';
export { provideReportDomain } from './report.providers';
export { provideBackupDomain } from './backup.providers';
export { providePlannedExpenseDomain } from './planned-expense.providers';
export { providePlannedIncomeDomain } from './planned-income.providers';
export { provideBankImportDomain } from './bank-import.providers';
export { provideTransferMatchingDomain } from './transfer-matching.providers';
export { provideAuthDomain } from './auth.providers';
export { provideDekDomain } from './dek.providers';
export { provideTagDomain } from './tag.providers';
